import { useState, useEffect } from "react";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from "@/components/ui/dialog";

import { Copy, ExternalLink, Code, Globe, Loader2, Trash2, QrCode, Download, Printer, X } from "lucide-react";
import { useToast } from "@/components/ui/use-toast";
import { getProducts, getCustomForms, generateIntegrationToken, type Product, type CustomForm, type GeneratedTokenResponse } from "@/services/api";
import QRCode from 'qrcode';

interface GeneratedLink {
  id: string;
  productId: string;
  productName: string;
  customFormId?: string;
  customFormName?: string;
  fullUrl: string;
  token: string;
  expiresAt: string | null;
  createdAt: Date;
  qrCode?: string;
}

const IntegrationPage = () => {
  const [activeTab, setActiveTab] = useState("quick-links");
  const [products, setProducts] = useState<Product[]>([]);
  const [customForms, setCustomForms] = useState<CustomForm[]>([]);
  const [generatedLinks, setGeneratedLinks] = useState<GeneratedLink[]>([]);
  const [loading, setLoading] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<string>("");
  const [selectedCustomForm, setSelectedCustomForm] = useState<string>("");
  const [selectedExpiration, setSelectedExpiration] = useState<string>("720"); // 30 days default
  const [customExpirationHours, setCustomExpirationHours] = useState<string>("");
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [selectedQrCode, setSelectedQrCode] = useState<{ qrCode: string; productName: string } | null>(null);

  const { toast } = useToast();

  // Load products and custom forms on mount
  useEffect(() => {
    const loadData = async () => {
      try {
        const [productsData, formsData] = await Promise.all([
          getProducts(),
          getCustomForms()
        ]);
        setProducts(productsData);
        setCustomForms(formsData);
      } catch (error) {
        console.error('Error loading data:', error);
        toast({
          title: "Error",
          description: "Failed to load products and forms.",
          variant: "destructive",
        });
      }
    };
    loadData();
  }, [toast]);

  // Filter custom forms by selected product
  const filteredCustomForms = customForms.filter(form => 
    !selectedProduct || form.productId === selectedProduct
  );

  const handleCopyToClipboard = (text: string) => {
    navigator.clipboard.writeText(text);
    toast({
      title: "Copied to clipboard",
      description: "The link has been copied to your clipboard.",
    });
  };

  const getExpirationHours = (): number | undefined => {
    if (selectedExpiration === "custom") {
      const hours = parseInt(customExpirationHours);
      return isNaN(hours) ? undefined : hours;
    }
    return parseInt(selectedExpiration);
  };

  const generateLink = async (isCustomForm: boolean = false) => {
    if (!selectedProduct) {
      toast({
        title: "Error",
        description: "Please select a product.",
        variant: "destructive",
      });
      return;
    }

    if (isCustomForm && !selectedCustomForm) {
      toast({
        title: "Error", 
        description: "Please select a custom form.",
        variant: "destructive",
      });
      return;
    }

    const expirationHours = getExpirationHours();
    if (selectedExpiration === "custom" && !expirationHours) {
      toast({
        title: "Error",
        description: "Please enter a valid expiration time in hours.",
        variant: "destructive",
      });
      return;
    }

    setLoading(true);
    try {
      const response = await generateIntegrationToken({
        productId: selectedProduct,
        customFormId: isCustomForm ? selectedCustomForm : undefined,
        expirationHours,
      });

      let qrCode: string | undefined;
      try {
        qrCode = await generateQRCode(response.fullUrl);
      } catch (error) {
        console.error('Failed to generate QR code:', error);
        toast({
          title: "Warning",
          description: "Link generated successfully, but QR code generation failed.",
          variant: "destructive",
        });
      }

      const newLink: GeneratedLink = {
        id: Date.now().toString(),
        productId: selectedProduct,
        productName: response.productName,
        customFormId: isCustomForm ? selectedCustomForm : undefined,
        customFormName: response.customFormName,
        fullUrl: response.fullUrl,
        token: response.token,
        expiresAt: response.expiresAt,
        createdAt: new Date(),
        qrCode,
      };

      setGeneratedLinks(prev => [newLink, ...prev]);
      
      toast({
        title: "Link generated",
        description: "Your feedback collection link has been generated successfully.",
      });
    } catch (error) {
      console.error('Error generating link:', error);
      toast({
        title: "Error",
        description: "Failed to generate link. Please try again.",
        variant: "destructive",
      });
    } finally {
      setLoading(false);
    }
  };

  const removeLink = (id: string) => {
    setGeneratedLinks(prev => prev.filter(link => link.id !== id));
    toast({
      title: "Link removed",
      description: "The link has been removed from the list.",
    });
  };

  const openLink = (url: string) => {
    window.open(url, '_blank');
  };

  const openQrDialog = (qrCode: string, productName: string) => {
    setSelectedQrCode({ qrCode, productName });
    setQrDialogOpen(true);
  };

  const generateQRCode = async (url: string): Promise<string> => {
    try {
      return await QRCode.toDataURL(url, {
        width: 256,
        margin: 2,
        color: {
          dark: '#000000',
          light: '#FFFFFF'
        }
      });
    } catch (error) {
      console.error('Error generating QR code:', error);
      throw new Error('Failed to generate QR code');
    }
  };

  const downloadQRCode = async (qrCodeDataUrl: string, productName: string) => {
    try {
      const link = document.createElement('a');
      link.download = `qr-code-${productName.toLowerCase().replace(/\s+/g, '-')}.png`;
      link.href = qrCodeDataUrl;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      
      toast({
        title: "QR Code Downloaded",
        description: "QR code has been downloaded successfully.",
      });
    } catch (error) {
      console.error('Error downloading QR code:', error);
      toast({
        title: "Error",
        description: "Failed to download QR code.",
        variant: "destructive",
      });
    }
  };

  const printQRCode = (qrCodeDataUrl: string, productName: string) => {
    try {
      const printWindow = window.open('', '_blank');
      if (printWindow) {
        printWindow.document.write(`
          <html>
            <head>
              <title>QR Code - ${productName}</title>
              <style>
                body { 
                  font-family: Arial, sans-serif; 
                  text-align: center; 
                  padding: 20px;
                  margin: 0;
                }
                .qr-container {
                  display: inline-block;
                  border: 2px solid #333;
                  padding: 20px;
                  border-radius: 8px;
                }
                .qr-code {
                  max-width: 300px;
                  height: auto;
                }
                .product-name {
                  font-size: 18px;
                  font-weight: bold;
                  margin-top: 10px;
                  color: #333;
                }
                .instructions {
                  font-size: 14px;
                  color: #666;
                  margin-top: 10px;
                }
              </style>
            </head>
            <body>
              <div class="qr-container">
                <img src="${qrCodeDataUrl}" alt="QR Code" class="qr-code" />
                <div class="product-name">${productName}</div>
                <div class="instructions">Scan to provide feedback</div>
              </div>
            </body>
          </html>
        `);
        printWindow.document.close();
        printWindow.print();
      }
    } catch (error) {
      console.error('Error printing QR code:', error);
      toast({
        title: "Error",
        description: "Failed to print QR code.",
        variant: "destructive",
      });
    }
  };

  const formatExpiration = (expiresAt: string | null): string => {
    if (!expiresAt) return "Never";
    const date = new Date(expiresAt);
    const now = new Date();
    const diffMs = date.getTime() - now.getTime();
    const diffDays = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    
    if (diffDays < 0) return "Expired";
    if (diffDays === 0) return "Today";
    if (diffDays === 1) return "Tomorrow";
    return `${diffDays} days`;
  };

  return (
    <div className="container mx-auto p-6 space-y-6">
      <div className="space-y-2">
        <p className="text-muted-foreground">
          Generate feedback collection links and integrate them into your applications.
        </p>
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="space-y-6">
        <TabsList className="grid w-full grid-cols-2">
          <TabsTrigger value="quick-links" className="flex items-center gap-2">
            <Globe className="h-4 w-4" />
            Quick Links
          </TabsTrigger>
          <TabsTrigger value="api-integration" className="flex items-center gap-2">
            <Code className="h-4 w-4" />
            API Integration
          </TabsTrigger>
        </TabsList>

        <TabsContent value="quick-links" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Globe className="h-5 w-5" />
                Quick Links
              </CardTitle>
              <CardDescription>
                Generate ready-to-use feedback links for marketing emails, support tickets, social media posts, and quick testing.
              </CardDescription>
              <div className="mt-6 mb-2 p-4 bg-secondary/70 border border-border rounded-lg">
                <p className="text-sm text-foreground">
                  <span className="font-medium">💡 Note:</span> Generated links are stored temporarily for this session only. 
                  They'll be cleared when you logout or close your browser. The links themselves remain valid until their expiration time.
                </p>
              </div>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* Generic Feedback Section */}
              <div className="space-y-4">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Generic Feedback Links</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Collect open comments and ratings for a product without a custom form.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                  <div className="space-y-2">
                    <Label htmlFor="product-select">Product</Label>
                    <Select value={selectedProduct} onValueChange={setSelectedProduct}>
                      <SelectTrigger id="product-select">
                        <SelectValue placeholder="Select a product..." />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="expiration-select">Expiration</Label>
                    <Select value={selectedExpiration} onValueChange={setSelectedExpiration}>
                      <SelectTrigger id="expiration-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 hour</SelectItem>
                        <SelectItem value="24">1 day</SelectItem>
                        <SelectItem value="168">7 days</SelectItem>
                        <SelectItem value="720">30 days</SelectItem>
                        <SelectItem value="4320">6 months</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      className="w-full" 
                      onClick={() => generateLink(false)}
                      disabled={loading || !selectedProduct}
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Generate Link
                    </Button>
                  </div>
                </div>
                
                {selectedExpiration === "custom" && (
                  <div className="mb-4">
                    <Label htmlFor="custom-hours">Custom expiration (hours)</Label>
                    <Input
                      id="custom-hours"
                      type="number"
                      placeholder="Enter hours..."
                      value={customExpirationHours}
                      onChange={(e) => setCustomExpirationHours(e.target.value)}
                      className="mt-1"
                    />
                  </div>
                )}

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product Name</TableHead>
                      <TableHead>Generated Link</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {generatedLinks
                      .filter(link => !link.customFormId)
                      .map((link) => (
                        <TableRow key={link.id}>
                          <TableCell className="font-medium">{link.productName}</TableCell>
                          <TableCell>
                            <div className="flex items-center gap-2">
                              <code className="bg-muted px-2 py-1 rounded text-xs max-w-[400px] truncate">
                                {link.fullUrl}
                              </code>
                            </div>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{formatExpiration(link.expiresAt)}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCopyToClipboard(link.fullUrl)}
                                title="Copy link"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openLink(link.fullUrl)}
                                title="Open link"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                              {link.qrCode && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openQrDialog(link.qrCode!, link.productName)}
                                  title="View QR Code"
                                >
                                  <QrCode className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => removeLink(link.id)}
                                title="Remove link"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    {generatedLinks.filter(link => !link.customFormId).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={4} className="text-center text-muted-foreground py-8">
                          No generic feedback links generated yet. Create one using the form above.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>

              {/* Custom Forms Section */}
              <div className="space-y-4 border-t pt-6">
                <div>
                  <h3 className="text-lg font-semibold mb-2">Custom Form Links</h3>
                  <p className="text-sm text-muted-foreground mb-4">
                    Collect structured feedback using predefined questions.
                  </p>
                </div>

                <div className="grid grid-cols-1 md:grid-cols-4 gap-4 mb-4">
                  <div className="space-y-2">
                    <Label htmlFor="form-product-select">Product</Label>
                    <Select value={selectedProduct} onValueChange={(value) => {
                      setSelectedProduct(value);
                      setSelectedCustomForm(""); // Reset form selection when product changes
                    }}>
                      <SelectTrigger id="form-product-select">
                        <SelectValue placeholder="Select product..." />
                      </SelectTrigger>
                      <SelectContent>
                        {products.map((product) => (
                          <SelectItem key={product.id} value={product.id}>
                            {product.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="form-select">Custom Form</Label>
                    <Select value={selectedCustomForm} onValueChange={setSelectedCustomForm} disabled={!selectedProduct}>
                      <SelectTrigger id="form-select">
                        <SelectValue placeholder="Select a form..." />
                      </SelectTrigger>
                      <SelectContent>
                        {filteredCustomForms.map((form) => (
                          <SelectItem key={form.id} value={form.id}>
                            {form.name}
                          </SelectItem>
                        ))}
                        {filteredCustomForms.length === 0 && selectedProduct && (
                          <div className="p-2 text-sm text-muted-foreground">
                            No forms available for this product
                          </div>
                        )}
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="form-expiration-select">Expiration</Label>
                    <Select value={selectedExpiration} onValueChange={setSelectedExpiration}>
                      <SelectTrigger id="form-expiration-select">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="1">1 hour</SelectItem>
                        <SelectItem value="24">1 day</SelectItem>
                        <SelectItem value="168">7 days</SelectItem>
                        <SelectItem value="720">30 days</SelectItem>
                        <SelectItem value="4320">6 months</SelectItem>
                        <SelectItem value="custom">Custom</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                  <div className="flex items-end">
                    <Button 
                      className="w-full"
                      onClick={() => generateLink(true)}
                      disabled={loading || !selectedProduct || !selectedCustomForm}
                    >
                      {loading ? <Loader2 className="h-4 w-4 animate-spin mr-2" /> : null}
                      Generate Link
                    </Button>
                  </div>
                </div>

                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Form Name</TableHead>
                      <TableHead>Product</TableHead>
                      <TableHead>Generated Link</TableHead>
                      <TableHead>Expires</TableHead>
                      <TableHead>Actions</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {generatedLinks
                      .filter(link => link.customFormId)
                      .map((link) => (
                        <TableRow key={link.id}>
                          <TableCell className="font-medium">{link.customFormName}</TableCell>
                          <TableCell>{link.productName}</TableCell>
                          <TableCell>
                            <code className="bg-muted px-2 py-1 rounded text-xs max-w-[300px] truncate">
                              {link.fullUrl}
                            </code>
                          </TableCell>
                          <TableCell>
                            <Badge variant="outline">{formatExpiration(link.expiresAt)}</Badge>
                          </TableCell>
                          <TableCell>
                            <div className="flex gap-2">
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => handleCopyToClipboard(link.fullUrl)}
                                title="Copy link"
                              >
                                <Copy className="h-4 w-4" />
                              </Button>
                              <Button 
                                size="sm" 
                                variant="outline"
                                onClick={() => openLink(link.fullUrl)}
                                title="Open link"
                              >
                                <ExternalLink className="h-4 w-4" />
                              </Button>
                              {link.qrCode && (
                                <Button
                                  size="sm"
                                  variant="outline"
                                  onClick={() => openQrDialog(link.qrCode!, link.productName)}
                                  title="View QR Code"
                                >
                                  <QrCode className="h-4 w-4" />
                                </Button>
                              )}
                              <Button
                                size="sm"
                                variant="outline"
                                onClick={() => removeLink(link.id)}
                                title="Remove link"
                              >
                                <Trash2 className="h-4 w-4" />
                              </Button>
                            </div>
                          </TableCell>
                        </TableRow>
                      ))}
                    {generatedLinks.filter(link => link.customFormId).length === 0 && (
                      <TableRow>
                        <TableCell colSpan={5} className="text-center text-muted-foreground py-8">
                          No custom form links generated yet. Create one using the form above.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>

        <TabsContent value="api-integration" className="space-y-6">
          <Card>
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Code className="h-5 w-5" />
                API Integration
              </CardTitle>
              <CardDescription>
                Integrate feedback collection into your applications using our token generation API.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              {/* API Documentation */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Token Generation API</h3>
                <div className="bg-muted p-4 rounded-lg">
                  <h4 className="font-medium mb-2">Generate Token</h4>
                  <code className="text-sm">POST /api/tokens/generate</code>
                  <div className="mt-3 space-y-2">
                    <div>
                      <strong>Headers:</strong>
                      <pre className="bg-background p-2 rounded text-xs mt-1">
{`Authorization: Bearer {your_shared_secret}
Content-Type: application/json`}
                      </pre>
                    </div>
                    <div>
                      <strong>Request Body:</strong>
                      <pre className="bg-background p-2 rounded text-xs mt-1">
{`{
  "productId": "your-product-uuid",
  "customFormId": "optional-form-uuid",
  "expiration": 3600,
  "scope": "feedback-submit"
}`}
                      </pre>
                    </div>
                    <div>
                      <strong>Response:</strong>
                      <pre className="bg-background p-2 rounded text-xs mt-1">
{`{
  "token": "eyJhbGciOiJIUzI1NiIs...",
  "expiresAt": "2024-01-15T10:30:00Z"
}`}
                      </pre>
                    </div>
                  </div>
                </div>
              </div>

              {/* Code Examples */}
              <div className="space-y-4">
                <h3 className="text-lg font-semibold">Code Examples</h3>
                
                <div className="space-y-4">
                  <div>
                    <h4 className="font-medium mb-2">JavaScript/Node.js</h4>
                    <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
{`// Generate token in your application
const response = await fetch('/api/tokens/generate', {
  method: 'POST',
  headers: {
    'Authorization': 'Bearer \${YOUR_SHARED_SECRET}',
    'Content-Type': 'application/json'
  },
  body: JSON.stringify({
    productId: 'your-product-uuid',
    expiration: 3600 // 1 hour
  })
});

const { token } = await response.json();
const feedbackUrl = \`https://feedback.yourcompany.com/?token=\${token}\`;

// Use the URL in your application
window.open(feedbackUrl, '_blank');`}
                    </pre>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">Python</h4>
                    <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
{`import requests
import json

# Generate token
response = requests.post(
    'https://your-api.com/api/tokens/generate',
    headers={
        'Authorization': f'Bearer {YOUR_SHARED_SECRET}',
        'Content-Type': 'application/json'
    },
    json={
        'productId': 'your-product-uuid',
        'expiration': 3600
    }
)

data = response.json()
feedback_url = f"https://feedback.yourcompany.com/?token={data['token']}"
print(feedback_url)`}
                    </pre>
                  </div>

                  <div>
                    <h4 className="font-medium mb-2">cURL</h4>
                    <pre className="bg-muted p-4 rounded-lg text-sm overflow-x-auto">
{`curl -X POST https://your-api.com/api/tokens/generate \\
  -H "Authorization: Bearer YOUR_SHARED_SECRET" \\
  -H "Content-Type: application/json" \\
  -d '{
    "productId": "your-product-uuid",
    "expiration": 3600
  }'`}
                    </pre>
                  </div>
                </div>
              </div>

              {/* Integration Patterns */}
              <div className="space-y-4 border-t pt-6">
                <h3 className="text-lg font-semibold">Common Integration Patterns</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Email Campaigns</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3">
                        Generate a unique token for each email recipient
                      </p>
                      <code className="text-xs bg-muted p-2 rounded block">
                        Generate token → Embed in email → Track responses
                      </code>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Website Buttons</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3">
                        Generate token on button click for instant feedback
                      </p>
                      <code className="text-xs bg-muted p-2 rounded block">
                        onClick → Generate token → Open feedback form
                      </code>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Mobile Apps</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3">
                        Generate token per user session with context
                      </p>
                      <code className="text-xs bg-muted p-2 rounded block">
                        Session start → Generate token → Store for later use
                      </code>
                    </CardContent>
                  </Card>

                  <Card>
                    <CardHeader>
                      <CardTitle className="text-base">Surveys</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <p className="text-sm text-muted-foreground mb-3">
                        Generate tokens for survey distribution
                      </p>
                      <code className="text-xs bg-muted p-2 rounded block">
                        Survey creation → Generate batch tokens → Distribute
                      </code>
                    </CardContent>
                  </Card>
                </div>
              </div>

              {/* Your Products Reference */}
              <div className="space-y-4 border-t pt-6">
                <h3 className="text-lg font-semibold">Your Products Reference</h3>
                <p className="text-sm text-muted-foreground">
                  Use these product IDs in your API calls:
                </p>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Product Name</TableHead>
                      <TableHead>Product ID</TableHead>
                      <TableHead>Example API Call</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {products.map((product) => (
                      <TableRow key={product.id}>
                        <TableCell className="font-medium">{product.name}</TableCell>
                        <TableCell>
                          <code className="bg-muted px-2 py-1 rounded text-xs">
                            {product.id}
                          </code>
                        </TableCell>
                        <TableCell>
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={() => handleCopyToClipboard(JSON.stringify({
                              productId: product.id,
                              expirationHours: 24
                            }, null, 2))}
                          >
                            <Copy className="h-4 w-4 mr-1" />
                            Copy JSON
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                    {products.length === 0 && (
                      <TableRow>
                        <TableCell colSpan={3} className="text-center text-muted-foreground py-8">
                          No products available. Create some products first to see examples.
                        </TableCell>
                      </TableRow>
                    )}
                  </TableBody>
                </Table>
              </div>
            </CardContent>
          </Card>
        </TabsContent>
      </Tabs>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onOpenChange={setQrDialogOpen}>
        <DialogContent className="sm:max-w-md">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <QrCode className="h-5 w-5" />
              QR Code - {selectedQrCode?.productName}
            </DialogTitle>
          </DialogHeader>
          <div className="flex flex-col items-center space-y-4">
            {selectedQrCode && (
              <>
                <div className="p-4 border rounded-lg">
                  <img 
                    src={selectedQrCode.qrCode} 
                    alt="QR Code" 
                    className="w-64 h-64 mx-auto"
                  />
                </div>
                <div className="flex gap-2">
                  <Button
                    onClick={() => downloadQRCode(selectedQrCode.qrCode, selectedQrCode.productName)}
                    className="flex items-center gap-2"
                  >
                    <Download className="h-4 w-4" />
                    Download
                  </Button>
                  <Button
                    variant="outline"
                    onClick={() => printQRCode(selectedQrCode.qrCode, selectedQrCode.productName)}
                    className="flex items-center gap-2"
                  >
                    <Printer className="h-4 w-4" />
                    Print
                  </Button>
                </div>
                <p className="text-sm text-muted-foreground text-center">
                  Scan this QR code to access the feedback form for {selectedQrCode.productName}
                </p>
              </>
            )}
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default IntegrationPage;
